'use client'

import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { type ChangeEvent, createContext, useCallback, useContext, useMemo, useState } from 'react'

import store from '@/lib/configs/store-config'
import {
  addedForeignDecimalMark,
  createNumberFormatter,
  isTypingOf,
  localizeSeparators,
  type NumberFormatter,
  parseNumberInput,
  SEPARATOR_STYLES,
} from '@/lib/number-format'
import { toast } from '@/lib/toast'
import { hideBalancesAtom, numberFormatAtom } from '@/state/storage'

/**
 * True under a DataTable: numbers whose fraction opens with 3+ zeros render as "0.0₃42", so a
 * tiny value (coin PnL, a sub-cent price, a dust size) stays narrow in a column. Elsewhere —
 * order inputs, headers, tooltips outside tables — numbers keep their plain form.
 */
export const CompactSmallNumbersContext = createContext(false)

/** The user's number formatter. Components re-render when the separator style or the
 *  hide-balances flag changes, so masking takes effect the moment it's toggled. */
export function useNumberFormat(): NumberFormatter {
  const prefs = useAtomValue(numberFormatAtom)
  const hidden = useAtomValue(hideBalancesAtom)
  const compactZeros = useContext(CompactSmallNumbersContext)
  return useMemo(
    () => createNumberFormatter(prefs, hidden, { compactZeros }),
    [prefs, hidden, compactZeros]
  )
}

/** Snapshot for code outside React (chart overlays, grid formatters). Callers must redraw on
 *  change themselves — subscribe with store.sub(numberFormatAtom / hideBalancesAtom). */
export function getNumberFormatter(): NumberFormatter {
  return createNumberFormatter(store.get(numberFormatAtom), store.get(hideBalancesAtom))
}

/**
 * Bridges a number field to the separator setting while its state stays canonical en-US
 * ("1,234.5" or "1234.5"): `display` is what the <input> shows, and `parse` turns what the
 * user typed back into canonical digits, or null for a keystroke to drop (see
 * parseNumberInput). Steppers, max-size and orderbook clicks keep writing en-US directly.
 *
 * While the owner's value still spells what was typed, the typed text is shown as-is, so an
 * owner that stores a number per keystroke doesn't eat a trailing "," or "-".
 *
 * A refused "," or "." that isn't the style's decimal mark (a German user on 1,234.56 typing
 * "1234,5") pops a hint naming the right mark, so the dropped keystroke never goes unnoticed.
 *
 * `bind(setValue)` spreads onto a plain field: `<Input {...size.bind(setSize)} />`.
 */
export function useNumberInput(
  value: string | number,
  { allowNegative = false, integer = false } = {}
) {
  const t = useTranslations('common')
  const { style } = useNumberFormat()
  const [typed, setTyped] = useState<{ text: string; canonical: string } | null>(null)
  const stored = String(value ?? '').replace(/,/g, '')
  const display =
    typed && isTypingOf(typed.canonical, stored)
      ? typed.text
      : localizeSeparators(String(value ?? ''), style)
  const parse = useCallback(
    (text: string) => {
      const canonical = parseNumberInput(text, display, style, { allowNegative, integer })
      if (canonical !== null) setTyped({ text, canonical })
      else if (addedForeignDecimalMark(text, display, style)) {
        const mark = SEPARATOR_STYLES[style].decimal
        toast.info(t('decimalSeparatorHint', { mark }), {
          id: 'decimal-separator-hint',
          position: 'bottom-right',
        })
      }
      return canonical
    },
    [display, style, allowNegative, integer, t]
  )
  const bind = (onValue: (canonical: string) => void) => ({
    value: display,
    inputMode: 'decimal' as const,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      const canonical = parse(e.target.value)
      if (canonical !== null) onValue(canonical)
    },
  })
  return { display, parse, bind }
}
